import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { exportToCsv, type ColumnConfig } from "shared/common/utils/export";
import { bearerHeaders, saveBlobInSession } from "shared/sessionDownload";
import type BillingCapabilitiesStore from "../BillingCapabilitiesStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export const INVOICE_STATUSES = ["draft", "sent", "paid", "overdue", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export type StatusFilter = "all" | InvoiceStatus | "outstanding";

export type InvoiceSortKey =
  | "invoiceNumber"
  | "clientName"
  | "issueDate"
  | "dueDate"
  | "status"
  | "total";

/** Sort keys that read best newest/biggest-first on first click. */
const DESC_FIRST: ReadonlySet<InvoiceSortKey> = new Set([
  "issueDate",
  "dueDate",
  "total",
]);

export interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  clientId: string;
  issueDate: string;
  dueDate: string;
  status: string;
  subtotal: number | string;
  taxAmount: number | string;
  total: number | string;
  taxRate?: number | string;
  paidDate?: string | null;
  notes?: string | null;
}

/** Coerce the string tedious returns for a decimal column. */
export const num = (v: unknown, fallback = 0): number => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const formatCurrency = (v: unknown): string =>
  `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface LineItemForm {
  description: string;
  quantity: string;
  unitPrice: string;
}

export interface InvoiceForm {
  clientId: string;
  issueDate: string;
  dueDate: string;
  status: string;
  taxRate: string;
  notes: string;
  lineItems: LineItemForm[];
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const emptyForm = (): InvoiceForm => ({
  clientId: "",
  issueDate: todayIso(),
  // Left blank on purpose: the API fills it from the client's negotiated terms.
  dueDate: "",
  status: "draft",
  taxRate: "0",
  notes: "",
  lineItems: [{ description: "", quantity: "1", unitPrice: "0" }],
});

/** Statuses that still owe money — the default view for someone chasing cash. */
const OUTSTANDING = new Set(["sent", "overdue"]);

/** One exported invoice row: the on-screen columns, plus the money breakdown. */
export interface InvoiceExportRow extends Record<string, unknown> {
  invoiceNumber: string;
  client: string;
  issueDate: string;
  dueDate: string;
  paidDate: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  total: number;
}

/**
 * CSV shape for the invoice list.
 *
 * No `format` on any column ON PURPOSE: the toolkit's "currency"/"date"
 * formatters produce locale strings ("$1,234.00", "8/1/2026") that a spreadsheet
 * re-imports as text. Raw numbers and ISO dates are what a finance person can
 * actually pivot on, and they do not shift with the exporter's locale.
 */
export const INVOICE_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "invoiceNumber", label: "Invoice" },
  { key: "client", label: "Client" },
  { key: "issueDate", label: "Issued" },
  { key: "dueDate", label: "Due" },
  { key: "paidDate", label: "Paid" },
  { key: "status", label: "Status" },
  { key: "subtotal", label: "Subtotal" },
  { key: "taxAmount", label: "Tax" },
  { key: "total", label: "Total" },
];

/** Dates arrive as either a date-only string or a full ISO timestamp. */
const isoDate = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value).slice(0, 10);

/**
 * The file name the API asked the browser to save as.
 *
 * The invoice PDF endpoint sends `Content-Disposition: attachment;
 * filename*=UTF-8''<percent-encoded>` (RFC 5987, the same encoding the
 * documents download endpoint uses). A blob download is initiated by this page,
 * so the header is advisory — the anchor's `download` attribute is what the
 * browser honours — which is exactly why it has to be read back rather than
 * assumed. Returns null when the header is absent or unparseable.
 */
export const fileNameFromDisposition = (header: string | null): string | null => {
  if (!header) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim()) || null;
    } catch {
      return null;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() || null : null;
};

/**
 * The message a failed PDF request carries.
 *
 * The endpoint answers a refusal with a Feathers JSON error — "Missing:
 * COMPANY_ADDRESS", "Billing access requires…" — and that text is the whole
 * value of the failure. Falling back to the bare status code would turn an
 * actionable answer into "Download failed (503)".
 */
const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = (await response.clone().json()) as { message?: unknown };
    if (typeof body?.message === "string" && body.message.trim()) return body.message;
  } catch {
    // Not JSON — fall through to the status line.
  }
  return `Could not generate the invoice PDF (${response.status})`;
};

/** Local fallback name, matching what the API would have sent. */
export const localPdfFileName = (invoiceNumber: unknown): string => {
  const safe = String(invoiceNumber ?? "")
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `Invoice-${safe || "invoice"}.pdf`;
};

/**
 * Route ViewStore for Billing → Invoices.
 *
 * Holds filter state and derives over the `invoices` ServiceStore cache. Per the
 * MobX rule, no API entity is stored here.
 */
export class InvoicesStore {
  rootStore: RootStore;

  search = "";
  statusFilter: StatusFilter = "all";
  clientFilter = "all";

  // Column sort (applied client-side over the filtered set, like the filters)
  sortKey: InvoiceSortKey = "issueDate";
  sortDir: SortDir = "desc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "invoices"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get clientsService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "clients"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  get loading(): boolean {
    return this.service?.isFindPending === true;
  }

  setSearch(value: string) {
    this.search = value;
  }

  setStatusFilter(value: StatusFilter) {
    this.statusFilter = value;
  }

  setClientFilter(value: string) {
    this.clientFilter = value;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (dates/amounts newest/biggest-first, text A→Z). */
  setSort(key: InvoiceSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  /** clientId → display name, for rendering a row without a join. */
  get clientNames(): Map<string, string> {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{
      id: string;
      name?: string;
    }>;
    return new Map(rows.map((c) => [String(c.id), c.name ?? "—"]));
  }

  clientName(clientId: string): string {
    return this.clientNames.get(String(clientId)) ?? "Unknown client";
  }

  get clientOptions(): Array<{ id: string; name: string }> {
    return [...this.clientNames.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get allInvoices(): InvoiceRow[] {
    const rows = (this.service?.entities ?? []) as unknown as InvoiceRow[];
    return rows
      .filter((r) => r?.id != null)
      .slice()
      .sort((a, b) => String(b.issueDate).localeCompare(String(a.issueDate)));
  }

  get invoices(): InvoiceRow[] {
    const term = this.search.trim().toLowerCase();
    return this.allInvoices.filter((invoice) => {
      if (this.clientFilter !== "all" && String(invoice.clientId) !== this.clientFilter) {
        return false;
      }
      if (this.statusFilter === "outstanding") {
        if (!OUTSTANDING.has(invoice.status)) return false;
      } else if (this.statusFilter !== "all" && invoice.status !== this.statusFilter) {
        return false;
      }
      if (!term) return true;
      return [invoice.invoiceNumber, this.clientName(invoice.clientId), invoice.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }

  /**
   * The rows both layouts render: filters, then the active column sort. Nulls
   * sort last in either direction. The mobile card list renders this too, so
   * the sort benefits both layouts without header cells on mobile.
   */
  get sortedInvoices(): InvoiceRow[] {
    const key = this.sortKey;
    const valueOf = (invoice: InvoiceRow): SortValue => {
      switch (key) {
        case "invoiceNumber":
          return textValue(invoice.invoiceNumber);
        case "clientName":
          return textValue(this.clientName(invoice.clientId));
        case "issueDate":
          return dateValue(invoice.issueDate);
        case "dueDate":
          return dateValue(invoice.dueDate);
        case "status":
          return textValue(invoice.status);
        case "total":
          return num(invoice.total);
      }
    };
    return sortRows(this.invoices, valueOf, this.sortDir);
  }

  /** Headline figures for the list header — computed over the FILTERED set. */
  get totals(): { count: number; billed: number; outstanding: number } {
    let billed = 0;
    let outstanding = 0;
    for (const invoice of this.invoices) {
      const total = num(invoice.total);
      billed += total;
      if (OUTSTANDING.has(invoice.status)) outstanding += total;
    }
    return { count: this.invoices.length, billed, outstanding };
  }

  /**
   * What "Export CSV" writes: the rows currently on screen, in the order they
   * are on screen — the active search, status/client filters and column sort all
   * apply. Exporting the unfiltered set would silently disagree with the header's
   * own totals, which are computed over the filtered set too.
   */
  get exportRows(): InvoiceExportRow[] {
    return this.sortedInvoices.map((invoice) => ({
      invoiceNumber: invoice.invoiceNumber,
      client: this.clientName(invoice.clientId),
      issueDate: isoDate(invoice.issueDate),
      dueDate: isoDate(invoice.dueDate),
      paidDate: isoDate(invoice.paidDate),
      status: invoice.status,
      subtotal: num(invoice.subtotal),
      taxAmount: num(invoice.taxAmount),
      total: num(invoice.total),
    }));
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Download the filtered list as CSV. Returns false instead of throwing when
   * there is nothing to export (the toolkit throws on an empty set) so the
   * header can stay a plain onClick.
   */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, INVOICE_EXPORT_COLUMNS, {
        fileName: `invoices-${todayIso()}`,
      });
      return true;
    } catch (err) {
      console.error("billing: CSV export failed", err);
      return false;
    }
  }

  /** Why the list itself did not load — the Body says so instead of an
   *  empty "No … yet." Null after a successful load. */
  loadError: string | null = null;
  /** The clients lookup failed: rows still render (names fall back to ids),
   *  but the client filter and the form's picker would be silently empty. */
  clientsError: string | null = null;

  /**
   * Re-read the list and the clients lookup. NEVER rejects: it is also the
   * reload after a successful write, and a failed reload must not turn that
   * write into "save failed". Failures land on `loadError` / `clientsError`.
   *
   * findOrThrow: the ServiceStore resolves null on a failed find (throwOnError
   * defaults to false), which rendered as "No invoices yet."
   */
  async refresh(): Promise<void> {
    runInAction(() => {
      this.loadError = null;
      this.clientsError = null;
    });
    const ended = sessionMark(this.rootStore);
    const [list, clients] = await Promise.allSettled([
      findOrThrow(this.service, { query: { $limit: 500, $sort: { issueDate: -1 } } }),
      findOrThrow(this.clientsService, { query: { $limit: 500 } }),
    ]);
    // One find can fail on its own just before a sign-out ends the other: that
    // failure is the previous person's, and the store has been reset under it.
    if (ended()) return;
    runInAction(() => {
      if (list.status === "rejected" && !isSessionEnded(list.reason)) {
        this.loadError = loadErrorMessage(list.reason, "Failed to load invoices");
      }
      if (clients.status === "rejected" && !isSessionEnded(clients.reason)) {
        this.clientsError = loadErrorMessage(clients.reason, "Failed to load clients");
      }
    });
    if (list.status === "rejected") console.error("billing: failed to load invoices", list.reason);
    if (clients.status === "rejected") console.error("billing: failed to load clients", clients.reason);
  }

  // ── Create / edit form ────────────────────────────────────────────────────

  /** Null while creating; the id of the invoice being edited otherwise. */
  editingId: string | null = null;
  form: InvoiceForm = emptyForm();
  saving = false;
  formError: string | null = null;
  formErrorDetails: string[] = [];

  get isEditing(): boolean {
    return this.editingId != null;
  }

  startCreate() {
    this.editingId = null;
    this.form = emptyForm();
    this.clearFormError();
  }

  async startEdit(invoice: InvoiceRow) {
    this.editingId = invoice.id;
    this.clearFormError();
    this.form = {
      clientId: String(invoice.clientId),
      issueDate: String(invoice.issueDate).slice(0, 10),
      dueDate: String(invoice.dueDate).slice(0, 10),
      status: invoice.status,
      taxRate: String(num(invoice.taxRate ?? 0)),
      notes: invoice.notes ?? "",
      lineItems: [],
    };
    // Rows live on their own service; pull the invoice's set for the editor.
    try {
      const api = this.rootStore.app as
        | { service: (p: string) => { find: (params?: object) => Promise<unknown> } }
        | undefined;
      const res = (await api
        ?.service("invoice-line-items")
        .find({ query: { invoiceId: invoice.id, $limit: 200, $sort: { sortOrder: 1 } } })) as
        | { data?: LineItemForm[] }
        | LineItemForm[]
        | undefined;
      const rows = (Array.isArray(res) ? res : (res?.data ?? [])) as LineItemForm[];
      runInAction(() => {
        this.form = {
          ...this.form,
          lineItems: rows.map((r) => ({
            description: String(r.description ?? ""),
            quantity: String(num(r.quantity, 1)),
            unitPrice: String(num(r.unitPrice, 0)),
          })),
        };
      });
    } catch (err) {
      console.error("billing: failed to load line items", err);
    }
  }

  setFormField(field: keyof InvoiceForm, value: string) {
    this.form = { ...this.form, [field]: value } as InvoiceForm;
    this.clearFormError();
  }

  addLineItem() {
    this.form = {
      ...this.form,
      lineItems: [...this.form.lineItems, { description: "", quantity: "1", unitPrice: "0" }],
    };
  }

  setLineItem(index: number, field: keyof LineItemForm, value: string) {
    const lineItems = this.form.lineItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    this.form = { ...this.form, lineItems };
    this.clearFormError();
  }

  removeLineItem(index: number) {
    this.form = {
      ...this.form,
      lineItems: this.form.lineItems.filter((_, i) => i !== index),
    };
  }

  private clearFormError() {
    this.formError = null;
    this.formErrorDetails = [];
  }

  /** Live preview of what the server will compute. Never sent — it recomputes. */
  get previewTotals(): { subtotal: number; taxAmount: number; total: number } {
    const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
    const subtotal = round2(
      this.form.lineItems.reduce(
        (sum, item) => sum + round2(num(item.quantity, 1) * num(item.unitPrice, 0)),
        0
      )
    );
    const taxAmount = round2(subtotal * num(this.form.taxRate));
    return { subtotal, taxAmount, total: round2(subtotal + taxAmount) };
  }

  get isFormValid(): boolean {
    return (
      !!this.form.clientId &&
      !!this.form.issueDate &&
      this.form.lineItems.some((item) => item.description.trim().length > 0) &&
      !this.saving
    );
  }

  /**
   * Save through the RAW feathers service, not the ServiceStore.
   *
   * ServiceStore.create/patch swallow server errors and resolve undefined, which
   * on a financial write would report a rejected invoice as saved.
   */
  async save(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = "Pick a client, an issue date, and at least one line item.";
      return false;
    }
    this.saving = true;
    this.clearFormError();
    try {
      const api = this.rootStore.app as
        | {
            service: (p: string) => {
              create(data: object): Promise<unknown>;
              patch(id: string, data: object): Promise<unknown>;
            };
          }
        | undefined;
      if (!api) throw new Error("Not connected to the API — reload and try again.");

      const payload: Record<string, unknown> = {
        clientId: this.form.clientId,
        issueDate: this.form.issueDate,
        status: this.form.status,
        taxRate: num(this.form.taxRate),
        notes: this.form.notes || null,
        lineItems: this.form.lineItems
          .filter((item) => item.description.trim().length > 0)
          .map((item) => ({
            description: item.description.trim(),
            quantity: num(item.quantity, 1),
            unitPrice: num(item.unitPrice, 0),
          })),
      };
      // Omit dueDate when blank so the API derives it from the client's terms.
      if (this.form.dueDate) payload.dueDate = this.form.dueDate;

      if (this.editingId) {
        await api.service("invoices").patch(this.editingId, payload);
      } else {
        await api.service("invoices").create(payload);
      }

      await this.refresh();
      runInAction(() => {
        this.form = emptyForm();
        this.editingId = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error && err.message ? err.message : "Failed to save the invoice";
        }
        const data = (err as { data?: { errors?: unknown } })?.data;
        if (!isSessionEnded(err)) {
          this.formErrorDetails = Array.isArray(data?.errors)
            ? data.errors.map((e) => String(e))
            : [];
        }
      });
      console.error("billing: save invoice failed", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  // ── Invoice PDF ───────────────────────────────────────────────────────────

  /** The invoice whose PDF is being fetched, so the row can show progress. */
  downloadingPdfId: string | null = null;

  private get capabilities(): BillingCapabilitiesStore | null {
    return (
      (this.rootStore as unknown as { billingCapabilitiesViewStore?: BillingCapabilitiesStore })
        .billingCapabilitiesViewStore ?? null
    );
  }

  /**
   * Whether this instance can produce an invoice PDF at all.
   *
   * The API owns the answer (`billing-capabilities`), exactly as the route gate
   * does — the same report, the same fail-closed default. An instance without a
   * company address would render a document with a blank "from", so the action
   * is offered disabled with the reason rather than producing one.
   */
  get canDownloadPdf(): boolean {
    return this.capabilities?.canDownloadInvoicePdf === true;
  }

  /** Why the action is disabled, naming the variables ops still has to set. */
  get pdfDisabledReason(): string {
    return (
      this.capabilities?.invoicePdfDisabledReason ??
      "Invoice PDFs are unavailable on this instance."
    );
  }

  isDownloadingPdf(invoice: InvoiceRow): boolean {
    return this.downloadingPdfId === invoice.id;
  }

  private notify(message: string, variant: "error" | "warning" | "success") {
    this.rootStore.notificationStore?.addNotification?.({ message, options: { variant } });
  }

  /**
   * Download one invoice as a PDF.
   *
   * Fetched with the Bearer header rather than opened as a plain link: the
   * endpoint is authenticated (finance role or admin) and a bare <a href> would
   * 401. The bytes are handed to the browser through a transient object URL —
   * the same shape as the documents download in ClientDetailStore, and the
   * reason both exist rather than a link.
   *
   * The document is built entirely server-side (line items are NOT in this
   * store's cache), so nothing here shapes the invoice; this only moves bytes.
   */
  async downloadPdf(invoice: InvoiceRow): Promise<boolean> {
    if (!this.canDownloadPdf) {
      this.notify(this.pdfDisabledReason, "warning");
      return false;
    }
    if (this.downloadingPdfId) return false;

    this.downloadingPdfId = invoice.id;
    // A raw fetch, so the session guard never sees it (shared/sessionDownload.ts):
    // a sign-out while it is out must not save the previous person's invoice
    // in the next person's session, nor toast the old token's 401 at them.
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = this.rootStore.authStore?.token ?? null;
      const response = await fetch(`${env.apiUrl}/invoices/${invoice.id}/pdf`, {
        headers: bearerHeaders(token),
      });
      if (ended()) return false;
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const blob = await response.blob();
      if (ended()) return false;
      if (blob.size === 0) throw new Error("The API returned an empty document.");

      return saveBlobInSession(
        ended,
        blob,
        fileNameFromDisposition(response.headers.get("Content-Disposition")) ??
          localPdfFileName(invoice.invoiceNumber)
      );
    } catch (err: unknown) {
      if (ended()) return false;
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Failed to download the invoice PDF";
      this.notify(message, "error");
      console.error("billing: invoice PDF download failed", err);
      return false;
    } finally {
      // reset() already cleared it; a newer session's download may hold it now.
      if (!ended()) {
        runInAction(() => {
          this.downloadingPdfId = null;
        });
      }
    }
  }

  /** Mark an invoice paid. The API stamps paidDate; it is not sent from here. */
  async markPaid(invoice: InvoiceRow): Promise<boolean> {
    try {
      const api = this.rootStore.app as
        | { service: (p: string) => { patch(id: string, data: object): Promise<unknown> } }
        | undefined;
      if (!api) throw new Error("Not connected to the API");
      await api.service("invoices").patch(invoice.id, { status: "paid" });
      await this.refresh();
      return true;
    } catch (err) {
      console.error("billing: mark paid failed", err);
      return false;
    }
  }

  /** Route entry. `refresh` never rejects; a failure shows on the page. */
  async initialize(): Promise<void> {
    await this.refresh();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.search = "";
    this.statusFilter = "all";
    this.clientFilter = "all";
    this.sortKey = "issueDate";
    this.sortDir = "desc";
    this.loadError = null;
    this.clientsError = null;
    this.editingId = null;
    this.form = emptyForm();
    this.saving = false;
    this.formError = null;
    this.formErrorDetails = [];
    this.downloadingPdfId = null;
  }
}

export default InvoicesStore;
